import React from 'react';
import PropTypes from 'prop-types';
import Chartist from 'chartist';
import 'chartist-plugin-tooltips';

import styled from 'styled-components';
import {connect} from 'react-redux';

import definitions from '../../../Style/LfStyleDefinitions';
import windowSizeObserver from '../../../Decorators/windowSizeObserver';
import {mediaQuery} from '../../../Style/cssHelpers';

import constants from '../constants';

//NOTE: chart styles found in chartist-settings.scss!

class PensionChart extends React.Component {
    constructor(props) {
        super(props);

        this.initializeChartSettings(props.data.chartData.chartSettings);
        this.chart = null;
    }

    static get propTypes() {
        return {
            chartId: PropTypes.string.isRequired
        }
    }

    componentDidMount() {
        const bigLabelCl = 'ct-series-big-label';
        const bigPercentageLabelCl = 'ct-series-big-percentages';
        const axisCl = 'ct-custom-axis';
        const lineCl = 'ct-big-percentage-line';    

        this.chart = new Chartist.Line(`#${this.props.chartId}`, this.data, this.settings);

        this.chart.on('draw', (data) => {
            // add class to first x- and y-axis in order to style
            if (data.type === 'grid') {
                if (data.index == 0) {
                    data.element.addClass(axisCl);
                }
            }

            // add big labels above "bars" on screens wider than ~mobile
            if (data.type === 'area' && this.props.windowWidth > definitions.breaks.small) {
                let pathData = data.element._node.getAttribute('d')
                    .split(/(?=[LMC])/)
                    .map(str => // ms edge path data contains spaces - replace for "," to match chrome/ff
                        str.trim().replace(/\s/g,',')
                    );

                const yLabelOffset = -25;
                let [x, y] = pathData[0]
                    .replace(/M\,*/g,'') // remove pathdata 'M' (and an optional extra "," inserted incorrectly for ms edge above)
                    .split(',')
                    .map(n => parseFloat(n));

                let {width, height} = data.element._node.getBoundingClientRect();
                let labelText = this.formatBigLabels(data.series.name);
                if (data.series.name === 'pensionSeries') {
                    let displayData = this.props.data.chartData.displayData;
                    const heightOffsetPercentage = ((displayData.totalPensionNoSavings+1) / displayData.totalPension);
                    height = (height) + height*heightOffsetPercentage;

                }

                data.group.elem('text', {
                    x: x + width/2,
                    y: y - (height/2),
                    style: 'text-anchor: middle'
                }, bigLabelCl).text(labelText);
            }

            // add big percentages on the right side of the chart
            if (data.type === 'area' && this.props.windowWidth > definitions.breaks.small) {
                let chartRect = this.chart.container.getBoundingClientRect();

                let pathData = data.element._node.getAttribute('d')
                    .split(/(?=[LMC])/)
                    .map(str => // ms edge path data contains spaces - replace for "," to match chrome/ff
                        str.trim().replace(/\s/g,',')
                    );

                const yLabelOffset = -5;
                let [x, y] = pathData[0]
                    .replace(/M\,*/g,'') // remove pathdata 'M' (and an optional extra "," inserted incorrectly for ms edge above)
                    .split(',')
                    .map(n => parseFloat(n));

                let {width, height} = data.element._node.getBoundingClientRect();
                let labelText = this.formatPercentageLabels(data.series.name);

                data.group.elem('line', {
                    x1: x,
                    x2: chartRect.width - 10,
                    y1: y - (height + 3 ),
                    y2: y - (height + 3 )
                }, lineCl);

                data.group.elem('text', {
                    x: chartRect.width + 20,
                    y: y - height - yLabelOffset,
                    style: 'text-anchor: middle'
                }, bigPercentageLabelCl).text(labelText);
            }
        });
    }

    componentDidUpdate(prevProps, prevState) {
        this.initializeChartSettings(this.props.data.chartData.chartSettings);
        this.chart.update(this.data, this.settings);
    }

    initializeChartSettings(chartSettings) {
        const displayData = this.props.data.chartData.displayData;
        const isDesktop =  this.props.windowWidth > definitions.breaks.small;
        const hasReachedAgeLimit = this.props.data.chartData.age >= constants.AGE_SAVINGS_LIMIT;
        const noPrivateSavingsPension = displayData.privateSavingsPension > 0;

        const hidePensionNoSavingsSeries = 
        this.data = {
            labels: chartSettings.labels,
            series: noPrivateSavingsPension || hasReachedAgeLimit
                ? [chartSettings.incomeSeries, chartSettings.pensionSeries, chartSettings.pensionNoSavingsSeries]
                : [chartSettings.incomeSeries, chartSettings.pensionSeries]
        };

        this.settings = {
            fullWidth: true,
            series: {
                incomeSeries: {
                    lineSmooth: Chartist.Interpolation.none(),
                    showArea: true
                },
                pensionSeries: {
                    lineSmooth: Chartist.Interpolation.none(),
                    showArea: true
                },
                pensionNoSavingsSeries: {
                    lineSmooth: Chartist.Interpolation.none(),
                    showArea: true
                }
            },
            axisX: {
                showGrid: isDesktop,
                showLabel: true,
                labelInterpolationFnc: (val, index) => 
                    val % 10 != 0 || index == 0 ? `${val}år` : ''
            },
            axisY: {
                showGrid: isDesktop,
                showLabel: isDesktop,
                type: Chartist.FixedScaleAxis,
                offset: 50, //100,
                labelInterpolationFnc: val => `${val}kr`,
                ...chartSettings.axisY
            },
            showPoint: false,
            plugins: [
                Chartist.plugins.tooltip({
                    pointClass: 'ct-area',
                    tooltipFnc: this.formatTooltip,
                    appendToBody: true
                })
            ]
        };
    }

    formatBigLabels = (seriesName) => {
        const data = this.props.data.chartData.displayData;

        switch(seriesName) {
            case 'incomeSeries':
                return `Lön`;

            case 'pensionSeries':
                return `Pension ${data.privateSavingsPension > 0 ? '(med eget sparande)' : ''}`;

            case 'pensionNoSavingsSeries':
                return `Pension ${data.privateSavingsPension > 0 ? '(utan eget sparande)' : ''}`;

            default:
                return '';
        }
    }
    formatPercentageLabels = (seriesName) => {
        const data = this.props.data.chartData.displayData;
        const privatePension = data.totalPension;
        const publicPension = data.totalPublicPension + data.occupationalPension;
        switch(seriesName) {
            case 'incomeSeries':
                return `100%`;

            case 'pensionSeries':
                return `${Math.round(privatePension / data.monthlySalary * 100)}%`;
                
            case 'pensionNoSavingsSeries':
                return `${Math.round(publicPension / data.monthlySalary * 100)}%`;
                
            default:
                return '';
        }
    }

    formatTooltip = (seriesName) => {
        const data = this.props.data.chartData.displayData;

        switch(seriesName) {
            case 'incomeSeries':
                return (`
                    <div class="chart-tooltip chart-tooltip--color-blue">
                        <h4>Nuvarande månadslön</h4>
                        ${data.monthlySalary} kr
                    </div>
                `);

            case 'pensionSeries':
                return (`
                    <div class="chart-tooltip chart-tooltip--color-green">
                        <h4>Din pension ${data.privateSavingsPension > 0 ? '(med eget sparande)' : ''}</h4>
                        <strong>Allmän pension</strong> ca. ${data.totalPublicPension} kr/mån<br/>
                        <strong>Tjänstepension</strong> ca. ${data.occupationalPension} kr/mån<br/>
                        ${data.privateSavingsPension > 0
                            ? `<strong>Eget sparande</strong> ca. ${data.privateSavingsPension} kr/mån<br/>`
                            : ''
                        }

                        <h5>Totalt:</h5> ca. ${data.totalPension} kr/mån<br/>
                    </div>
                `);

            case 'pensionNoSavingsSeries':
                return (`
                    <div class="chart-tooltip chart-tooltip--color-pink">
                        <h4>Din pension (utan eget sparande)</h4>
                        <strong>Allmän pension</strong> ca. ${data.totalPublicPension} kr/mån<br/>
                        <strong>Tjänstepension</strong> ca. ${data.occupationalPension} kr/mån<br/>

                        <h5>Totalt</h5> ca. ${data.totalPensionNoSavings} kr/mån<br/>
                    </div>
                `);

            default:
                return '';
        }
    }


    render() {
        const data = this.props.data.chartData.displayData;
        const hidePrivateSavings = this.props.data.chartData.age >= constants.AGE_SAVINGS_LIMIT;
        const classes = ['ct-chart','ct-octave']

        if (hidePrivateSavings) {
            classes.push('ct-hide-private-savings');
        }

        return (
            <ChartWrapper>
                <div
                    className={classes.join(' ')}
                    id={this.props.chartId}
                >
                </div>

                {/* note: bar color styling is defined in external stylesheet!
                    styles/vendor/chartist/chartist-settings.scss */}

                <ColorChartWrapper>
                    <ResponsiveBarColorChart color="#005FA0">
                        Lön<br />
                        <span>{data.monthlySalary} SEK/mån</span>
                    </ResponsiveBarColorChart>

                    <ResponsiveBarColorChart color="#EF3C76">
                        Pension, utan eget sparande<br />
                        <span>{data.totalPensionNoSavings} SEK/mån ({Math.round(data.totalPensionNoSavings/data.monthlySalary*100)}% av lönen)</span>
                    </ResponsiveBarColorChart>

                    {!hidePrivateSavings && (
                        <ResponsiveBarColorChart color="#00B04C">
                            Pension, med eget sparande<br />
                            <span>Pension + eget sparande ger</span><br />
                            <span>{data.totalPension} SEK/mån ({Math.round(data.totalPension/data.monthlySalary*100)}% av lönen)</span>
                        </ResponsiveBarColorChart>
                    )}
                </ColorChartWrapper>
            </ChartWrapper>
        );
    }
}

const mapStateToProps = (state) => {
    return {
        data: state.retirementGuideChartData
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(windowSizeObserver(PensionChart));

// styles
const ChartWrapper = styled.div`
    display: block;
    margin: 0 auto;

    width: 90%;
    max-width: 960px;
    padding-right: 40px;
    height: auto;

    ${mediaQuery.xsmall(`
        margin-right: 25px;
        max-width: 1000px;
        padding-right: 0px;
    `)}

    svg {
        overflow: visible;
    }
`;

const ColorChartWrapper = styled.div`
    position: relative;
    width: 100%;
    display: none;

    ${mediaQuery.small(`
        display: inline-block;
    `)}

    text-align: left;
`;

const ResponsiveBarColorChart = styled.div`
    position: relative;
    display: inline-block;
    padding-left: 25px;
    margin-bottom: 20px;
    font-family: ${definitions.fonts.heading} !important;
    font-weight: bold;
    font-size: 16px;
    
    & > span {
        font-size: 14px;
        font-weight: normal;
    }

    &:before {
        display: block;
        position: absolute;
        top: 6px;
        left: 8px;
        width: 10px;
        height: 10px;
        margin-right: 5px;
        content: "";
        background-color: ${props => props.color};
    }
`;
